import React, { useContext, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { routesConfig } from '../config/routesConfig';
import { hasAccess } from '../utils/permission';
import CartModal from './cart/CartModal';
import NotificationBell from './NotificationBell';
import InsightNavButton from './insights/InsightNavButton';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarsIcon from '@mui/icons-material/Stars';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InsightsIcon from '@mui/icons-material/Insights';
import Swal from 'sweetalert2';
import api from '../adapters/api';
import RestartCountdown, { showDeployLog } from './RestartCountdown';

const ISSUE_DESK_URL = process.env.REACT_APP_ISSUE_DESK_URL ?? 'https://ccs-issues.netlify.app/';

export default function NavBar() {
  const { currentUser, logout } = useContext(AuthContext);
  const { cart } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [anchorNav, setAnchorNav] = useState(null);
  const [anchorUser, setAnchorUser] = useState(null);
  const [showCart, setShowCart] = useState(false);

  const availMenu = routesConfig.filter(
    item => item.showInNav !== false && hasAccess(currentUser, item.permission)
  );

  const handleNavOpen = (e) => setAnchorNav(e.currentTarget);
  const handleNavClose = () => setAnchorNav(null);
  const handleUserOpen = (e) => setAnchorUser(e.currentTarget);
  const handleUserClose = () => setAnchorUser(null);
  const handleShowCart = () => setShowCart(true);
  const handleCloseCart = () => setShowCart(false);

  const initials = currentUser?.user_name?.charAt(0).toUpperCase() ?? '?';

  const handleScheduleRestart = async () => {
    handleUserClose();
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'อัปเดตและรีสตาร์ทระบบ?',
      text: 'ระบบจะดึงโค้ดล่าสุดแล้วรีสตาร์ท ผู้ใช้ทุกคนจะได้รับแจ้งเตือนล่วงหน้า 5 นาที และต้องเข้าสู่ระบบใหม่หลังอัปเดต',
      input: 'textarea',
      inputLabel: 'โน้ตประกาศถึงผู้ใช้ (ไม่บังคับ)',
      inputPlaceholder: 'เช่น อัปเดตระบบคำนวณค่าส่ง ใช้เวลาประมาณ 3 นาที',
      inputAttributes: { maxlength: 2000, rows: 6 },
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575',
      confirmButtonText: 'เริ่มนับถอยหลัง',
      cancelButtonText: 'ยกเลิก',
    });
    if (!confirm.isConfirmed) return;
    try {
      await api.post('/api/system/restart', { note: confirm.value || '' });
    } catch (err) {
      console.error('schedule restart error:', err);
      Swal.fire({
        icon: 'error',
        title: 'สั่งอัปเดตระบบไม่สำเร็จ',
        text: err.response?.data?.error || 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#1976d2',
        confirmButtonText: 'ตกลง',
      });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'primary.main',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}
      >
        <Toolbar sx={{ px: { xs: 1.5, md: 3 }, gap: 1 }}>

          {/* Mobile hamburger */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton color="inherit" onClick={handleNavOpen} size="small">
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorNav}
              open={Boolean(anchorNav)}
              onClose={handleNavClose}
              PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 200, borderRadius: 2 } }}
            >
              {availMenu.map(page => (
                <MenuItem
                  component={NavLink}
                  to={page.path}
                  key={page.path}
                  onClick={handleNavClose}
                  selected={location.pathname === page.path}
                  sx={{
                    gap: 1.5,
                    borderRadius: 1,
                    mx: 0.5,
                    my: 0.25,
                    '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.main', fontWeight: 600 },
                    '&.Mui-selected:hover': { bgcolor: 'primary.light' },
                  }}
                >
                  {page.icon}
                  {page.label}
                </MenuItem>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => { logout(); handleNavClose(); }}
                sx={{ gap: 1.5, borderRadius: 1, mx: 0.5, my: 0.25, color: 'error.main' }}
              >
                <LogoutIcon fontSize="small" color="error" />
                ออกจากระบบ
              </MenuItem>
            </Menu>
          </Box>

          {/* Logo + Company name */}
          <Box
            component={NavLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              textDecoration: 'none',
              color: 'inherit',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.55,
                bgcolor: '#fff',
                borderRadius: 2,
                fontWeight: 900,
                fontSize: '1rem',
                letterSpacing: '0.18em',
                lineHeight: 1,
                color: 'primary.main',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                fontFamily: 'Sarabun, sans-serif',
                userSelect: 'none',
              }}
            >
              CCS
            </Box>
            
          </Box>

          {/* Desktop nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, alignItems: 'center', gap: 0.5, ml: 2 }}>
            {availMenu.map(page => (
              <Button
                component={NavLink}
                to={page.path}
                key={page.path}
                startIcon={page.icon}
                sx={{
                  color: 'rgba(255,255,255,0.87)',
                  px: 2,
                  borderRadius: 5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' },
                  '&.active': { bgcolor: 'rgba(255,255,255,0.20)', color: '#fff', fontWeight: 700 },
                }}
              >
                {page.label}
              </Button>
            ))}
          </Box>

          {/* Mobile spacer */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

          {/* Right: Notifications + Cart + User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <RestartCountdown />
            {ISSUE_DESK_URL && (
              <Tooltip title="แจ้งปัญหา">
                <IconButton
                  component="a"
                  href={ISSUE_DESK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  aria-label="แจ้งปัญหา"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'rgba(255,255,255,0.10)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
                  }}
                >
                  <FeedbackOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <InsightNavButton />
            <NotificationBell />
            <Tooltip title="ตะกร้า">
              <IconButton
                color="inherit"
                onClick={handleShowCart}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'rgba(255,255,255,0.10)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
                }}
              >
                <Badge badgeContent={cart.length} color="error" invisible={cart.length === 0}>
                  <ShoppingCartIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Avatar + chevron */}
            <Tooltip title={currentUser?.user_name || ''}>
              <IconButton
                onClick={handleUserOpen}
                sx={{
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  borderRadius: 5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' },
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    border: '2px solid rgba(255,255,255,0.4)',
                  }}
                >
                  {initials}
                </Avatar>
                <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* User dropdown */}
          <Menu
            anchorEl={anchorUser}
            open={Boolean(anchorUser)}
            onClose={handleUserClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.25,
                minWidth: 190,
                borderRadius: 2.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              },
            }}
          >
            {/* User info */}
            <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                {initials}
              </Avatar>
              <Typography variant="body2" fontWeight={700} color="text.primary" noWrap>
                {currentUser?.user_name}{currentUser?.dept?.[0] ? ` (${currentUser.dept[0]})` : ''}
              </Typography>
            </Box>
            <Divider />
            {currentUser?.dept?.some(d => d.includes('ธุรการ')) || currentUser?.user_level >= 2 ? (
              <MenuItem
                onClick={() => { navigate('/notification-settings'); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <NotificationsActiveIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                ตั้งค่าการแจ้งเตือน
              </MenuItem>
            ) : null}
            {hasAccess(currentUser, { minLevel: 2 }) ? (
              <MenuItem
                onClick={() => { navigate('/promotions'); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <LocalOfferIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                โปรโมชัน
              </MenuItem>
            ) : null}
            {hasAccess(currentUser, { minLevel: 2 }) ? (
              <MenuItem
                onClick={() => { navigate('/loyalty'); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <StarsIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                แต้มสะสม
              </MenuItem>
            ) : null}
            {hasAccess(currentUser, { minLevel: 2 }) ? (
              <MenuItem
                onClick={() => { navigate('/shipping-settings'); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <LocalShippingIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                ตั้งค่าค่าขนส่ง
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => { navigate('/activity-log'); handleUserClose(); }}
              sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
            >
              <HistoryIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
              ประวัติการใช้งาน
            </MenuItem>
            {currentUser?.is_insight_support ? (
              <MenuItem
                onClick={() => { navigate('/insight-console'); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <InsightsIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                Insight Console
              </MenuItem>
            ) : null}
            {currentUser?.is_dev ? (
              <MenuItem
                onClick={handleScheduleRestart}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <RestartAltIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                อัปเดต / รีสตาร์ทระบบ
              </MenuItem>
            ) : null}
            {currentUser?.is_dev ? (
              <MenuItem
                onClick={() => { showDeployLog(); handleUserClose(); }}
                sx={{ py: 0.875, px: 1.5, gap: 1.25, fontSize: '0.875rem' }}
              >
                <HistoryIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                log การอัปเดตล่าสุด
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => { logout(); handleUserClose(); }}
              sx={{
                py: 0.875,
                px: 1.5,
                color: 'error.main',
                gap: 1.25,
                fontSize: '0.875rem',
                '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 17 }} />
              ออกจากระบบ
            </MenuItem>
          </Menu>

        </Toolbar>
      </AppBar>

      <CartModal show={showCart} handleClose={handleCloseCart} />
    </>
  );
}
